import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import {
  ArrowLeft, Loader2, Frame, MapPin, CalendarDays, Ticket as TicketIcon,
  FileText, X, Send, CheckCircle2, Mail, ExternalLink, Sparkles,
} from 'lucide-react';
import CustomCursor from '@/components/CustomCursor';
import CodeRain from '@/components/CodeRain';
import Header from '@/components/Header';
import { ROLE_OPTIONS } from '@/components/FooterForm';

const FALLBACK_BAF_URL = 'https://britishartfair.seetickets.com/tour/british-art-fair?OfferCode=GPDDIG4';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Date helpers ─────────────────────────────────────────────

function formatDate(iso, { day = '2-digit', month = 'short', year = 'numeric' } = {}) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day, month, year }).toUpperCase();
  } catch {
    return null;
  }
}

function formatDateRange(start, end) {
  if (!start && !end) return null;
  if (!end || start === end) return formatDate(start);
  const s = new Date(start);
  const e = new Date(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  if (sameMonth) {
    const day = s.getDate();
    const dayE = e.getDate();
    const month = s.toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
    return `${String(day).padStart(2, '0')}–${String(dayE).padStart(2, '0')} ${month} ${s.getFullYear()}`;
  }
  return `${formatDate(start)} — ${formatDate(end)}`;
}

// ─── Main page ────────────────────────────────────────────────

export default function PublicExhibitionPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [exhibition, setExhibition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(null); // 'tickets' | 'opencall' | null

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/public/exhibitions/${slug}`)
      .then((r) => setExhibition(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'Exhibition not found' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Respect deep-link hashes (#tickets opens the modal, #opencall scrolls to inline form)
  useEffect(() => {
    if (!exhibition) return;
    const h = (window.location.hash || '').replace('#', '').toLowerCase();
    if (h === 'tickets' && exhibition.ticket_requests_enabled) {
      setOpenModal('tickets');
    } else if (h === 'opencall' && exhibition.open_call_enabled) {
      // let the DOM settle then scroll to the inline form
      setTimeout(() => {
        document.getElementById('opencall')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [exhibition]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
        <CustomCursor /><Header />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
        <CustomCursor /><Header />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center">
            <Frame className="w-8 h-8 text-[#333] mx-auto" />
            <p className="text-[13px] font-mono text-[#888] mt-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-[#90E0EF] hover:text-white transition-colors"
            >
              ← BACK TO GALLERY
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dateRange = formatDateRange(exhibition.start_date, exhibition.end_date);
  const deadline = formatDate(exhibition.open_call_deadline);
  const showTickets = !!exhibition.ticket_requests_enabled;
  const showOpenCall = !!exhibition.open_call_enabled;
  const isBaf = exhibition.ticket_mode === 'baf_free';
  const ticketCtaLabel = isBaf ? 'Free tickets' : 'Request ticket';

  return (
    <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
      <CustomCursor />
      <CodeRain />
      <Header />
      <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: '#111', border: '1px solid #333', borderRadius: 0, fontFamily: '"JetBrains Mono", monospace', fontSize: '12px' } }} />

      <main className="relative z-10" style={{ paddingTop: 'calc(var(--header-height) + 24px)' }}>
        {/* Hero */}
        <section className="px-6 md:px-10 lg:px-16 py-16 md:py-24 border-b border-[#111]">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">
              THE RELATIVE GALLERY
            </p>
            {exhibition.theme && (
              <p className="mt-2 text-[11px] font-mono uppercase tracking-[0.28em] text-[#90E0EF]">
                — {exhibition.theme}
              </p>
            )}
            <h1
              className="mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-outfit font-bold text-white tracking-tighter leading-[0.98] uppercase"
              data-testid="exhibition-title"
            >
              {exhibition.title}
            </h1>

            {/* Info row */}
            <div className="mt-8 flex flex-wrap justify-center items-start gap-x-10 gap-y-4 text-[11px] font-mono text-left">
              {dateRange && (
                <div className="flex items-start gap-2" data-testid="exhibition-dates">
                  <CalendarDays className="w-3.5 h-3.5 text-[#666] mt-0.5" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[#555]">Dates</div>
                    <div className="text-[#e8e8e8]">{dateRange}</div>
                  </div>
                </div>
              )}
              {(exhibition.location_name || exhibition.city) && (
                <div className="flex items-start gap-2" data-testid="exhibition-location">
                  <MapPin className="w-3.5 h-3.5 text-[#666] mt-0.5" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[#555]">Where</div>
                    <div className="text-[#e8e8e8]">
                      {exhibition.location_name}
                      {exhibition.location_address && <><br /><span className="text-[#888]">{exhibition.location_address}</span></>}
                      {(exhibition.city || exhibition.country) && (
                        <><br /><span className="text-[#888]">{[exhibition.city, exhibition.country].filter(Boolean).join(', ')}</span></>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {deadline && showOpenCall && (
                <div className="flex items-start gap-2" data-testid="exhibition-deadline">
                  <FileText className="w-3.5 h-3.5 text-[#e4c07a] mt-0.5" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[#555]">Open call deadline</div>
                    <div className="text-[#e4c07a]">{deadline}</div>
                  </div>
                </div>
              )}
            </div>

            {/* CTAs */}
            {(showTickets || showOpenCall) && (
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                {showTickets && (
                  <button
                    type="button"
                    data-testid="exhibition-cta-tickets"
                    onClick={() => setOpenModal('tickets')}
                    className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 bg-[#90E0EF] text-[#050505] border border-[#90E0EF] hover:bg-white transition-colors"
                  >
                    <TicketIcon className="w-4 h-4" />
                    {ticketCtaLabel}
                  </button>
                )}
                {showOpenCall && (
                  <a
                    href="#opencall"
                    data-testid="exhibition-cta-opencall"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('opencall')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 bg-transparent text-[#90E0EF] border border-[#90E0EF] hover:bg-[#90E0EF]/8 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Apply — Open Call
                  </a>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Description / Statement — hidden when open call is on, since
            the inline open-call section becomes the single source of the
            recap (avoids duplicated body copy on the same page). */}
        {!showOpenCall && (exhibition.description || exhibition.statement) && (
          <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20 border-b border-[#111]">
            <div className="max-w-3xl mx-auto space-y-14">
              {exhibition.description && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666] mb-4">
                    About the exhibition
                  </div>
                  <p className="text-[15px] leading-[1.75] text-[#e8e8e8] whitespace-pre-line" data-testid="exhibition-description">
                    {exhibition.description}
                  </p>
                </div>
              )}
              {exhibition.statement && (
                <div className="border-t border-[#181818] pt-14">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666] mb-4">
                    Curatorial statement
                  </div>
                  <p className="text-[14px] leading-[1.85] text-[#f0f0f0] italic whitespace-pre-line" data-testid="exhibition-statement">
                    {exhibition.statement}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Inline Open Call — the primary action section when enabled */}
        {showOpenCall && (
          <OpenCallInlineSection exhibition={exhibition} />
        )}

        {/* Closing rail — repeat the Ticket CTA whenever tickets are enabled.
            Open call already ends with its own submit button inside the
            inline form, so we do NOT repeat the open-call CTA here. */}
        {showTickets && (
          <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">Ready?</p>
              <h2 className="mt-3 text-2xl md:text-3xl font-outfit font-semibold text-white tracking-tight uppercase">
                Take part.
              </h2>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  data-testid="exhibition-cta-tickets-bottom"
                  onClick={() => setOpenModal('tickets')}
                  className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 bg-[#90E0EF] text-[#050505] border border-[#90E0EF] hover:bg-white transition-colors"
                >
                  <TicketIcon className="w-4 h-4" /> {ticketCtaLabel}
                </button>
              </div>
            </div>
          </section>
        )}
      </main>

      <AnimatePresence>
        {openModal === 'tickets' && (
          <TicketModal
            exhibition={exhibition}
            onClose={() => setOpenModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Inline Open Call section — countdown + form directly on the page
// ═══════════════════════════════════════════════════════════════════

function OpenCallInlineSection({ exhibition }) {
  const deadline = exhibition.open_call_deadline;
  const closed = isDeadlinePassed(deadline);

  return (
    <section
      id="opencall"
      data-testid="exhibition-opencall-section"
      className="px-6 md:px-10 lg:px-16 pt-6 md:pt-8 pb-16 md:pb-24 border-b border-[#111] scroll-mt-24"
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">
          — Open Call · Apply
        </div>

        {/* Countdown — sits directly under the header, above the description */}
        {deadline && (
          <div className="mt-8">
            <CountdownTimer deadline={deadline} />
          </div>
        )}

        {exhibition.description && (
          <p
            className="mt-10 text-[14px] leading-[1.75] text-[#f0f0f0] whitespace-pre-line mx-auto"
            data-testid="opencall-description"
          >
            {exhibition.description}
          </p>
        )}

        {/* Form */}
        <div className="mt-12 text-left">
          {closed ? (
            <div className="border border-[#e28f8f]/30 bg-[#0a0a0a] p-6 text-center" data-testid="opencall-closed">
              <FileText className="w-6 h-6 text-[#e28f8f] mx-auto" />
              <p className="mt-3 text-[13px] font-outfit text-white">Open call closed</p>
              <p className="mt-1 text-[11px] font-mono text-[#666]">Applications are no longer being accepted for this exhibition.</p>
            </div>
          ) : (
            <OpenCallInlineForm exhibition={exhibition} />
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Countdown timer ──────────────────────────────────────────

function isDeadlinePassed(deadlineIso) {
  if (!deadlineIso) return false;
  return new Date(deadlineIso).getTime() < Date.now();
}

function useCountdown(deadlineIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadlineIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [deadlineIso]);
  if (!deadlineIso) return null;
  const diff = new Date(deadlineIso).getTime() - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds, expired: false };
}

function CountdownTimer({ deadline }) {
  const c = useCountdown(deadline);
  if (!c) return null;

  const dateLine = new Date(deadline).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).toUpperCase();

  if (c.expired) {
    return (
      <div className="border-t border-b border-[#181818] py-6 text-center" data-testid="opencall-countdown-expired">
        <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">Deadline was</div>
        <div className="mt-1 text-[13px] font-mono text-[#e28f8f]">{dateLine}</div>
      </div>
    );
  }

  return (
    <div className="border-t border-b border-[#181818] py-6" data-testid="opencall-countdown">
      <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666] text-center">
        Applications close in
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-4 text-center">
        <CountdownUnit value={c.days} label="Days" testId="cd-days" />
        <CountdownUnit value={c.hours} label="Hours" testId="cd-hours" />
        <CountdownUnit value={c.minutes} label="Minutes" testId="cd-minutes" />
        <CountdownUnit value={c.seconds} label="Seconds" pulse testId="cd-seconds" />
      </div>
      <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.24em] text-[#555] text-center">
        Deadline: {dateLine}
      </div>
    </div>
  );
}

function CountdownUnit({ value, label, pulse, testId }) {
  return (
    <div>
      <div
        data-testid={testId}
        className={`text-3xl sm:text-4xl md:text-5xl font-outfit font-semibold text-white tabular-nums leading-none ${pulse ? 'text-[#90E0EF]' : ''}`}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div className="mt-1.5 text-[9px] font-mono uppercase tracking-[0.28em] text-[#666]">
        {label}
      </div>
    </div>
  );
}

// ─── Inline Open Call form ────────────────────────────────────

// ─── Enum options — MUST match the DB CHECK constraints on
// open_call_applications.main_medium_style and .venue_attendance.
// Keep in sync with /app/frontend/src/pages/OpenCallPage.js
// ───────────────────────────────────────────────────────────
const MEDIUM_OPTIONS = [
  { value: 'digital_art', label: 'Digital Art' },
  { value: 'ai_art', label: 'AI Art' },
  { value: 'video', label: 'Video' },
  { value: 'photography', label: 'Photography' },
  { value: 'animation', label: 'Animation' },
  { value: 'immersive_media', label: 'Immersive Media' },
  { value: 'installation', label: 'Installation' },
  { value: 'new_media_art', label: 'New Media Art' },
  { value: 'other', label: 'Other' },
];

const ATTENDANCE_OPTIONS = [
  { value: 'yes', label: 'Yes, I can come to the venue' },
  { value: 'maybe', label: 'Maybe, unable to confirm at this time' },
  { value: 'remote', label: 'No, I can only participate remotely' },
];

function OpenCallInlineForm({ exhibition }) {
  const [form, setForm] = useState({
    artist_name: '',
    email: '',
    main_medium_style: '',
    location: '',
    main_social_media: '',
    portfolio_link: '',
    artist_bio: '',
    artist_statement: '',
    artwork_link: '',
    venue_attendance: '',
    newsletter_opt_in: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ['artist_name', 'email', 'main_medium_style', 'location', 'artist_statement', 'artwork_link', 'venue_attendance'];
    for (const k of required) {
      if (!form[k].trim()) {
        toast.error(`Please fill: ${k.replaceAll('_', ' ')}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/open-call-application?exhibition=${encodeURIComponent(exhibition.slug)}`,
        form,
      );
      setSuccess(true);
      // Scroll success into view so the user sees confirmation
      setTimeout(() => {
        document.getElementById('opencall-inline-success')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div id="opencall-inline-success" data-testid="opencall-inline-success" className="border border-[#7ed4a0]/30 bg-[#0a0a0a] py-12 px-6 text-center">
        <CheckCircle2 className="w-10 h-10 text-[#7ed4a0] mx-auto" />
        <h3 className="mt-4 text-lg font-outfit font-semibold text-white">Application received.</h3>
        <p className="mt-2 text-[13px] text-[#f0f0f0] leading-relaxed max-w-md mx-auto">
          Thank you for applying. We&apos;ll review your application and reach out with next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="opencall-inline-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Artist name">
          <input required data-testid="oc-inline-name" value={form.artist_name} onChange={(e) => set('artist_name', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Email">
          <input required type="email" data-testid="oc-inline-email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Main medium / style">
          <select
            required
            data-testid="oc-inline-medium"
            value={form.main_medium_style}
            onChange={(e) => set('main_medium_style', e.target.value)}
            className={inputCls}
          >
            <option value="">— select your main medium —</option>
            {MEDIUM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Location">
          <input required data-testid="oc-inline-location" value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="City, Country" className={inputCls} />
        </Field>
        <Field label="Main social media">
          <input data-testid="oc-inline-social" value={form.main_social_media} onChange={(e) => set('main_social_media', e.target.value)} placeholder="Optional" className={inputCls} />
        </Field>
        <Field label="Portfolio link">
          <input data-testid="oc-inline-portfolio" value={form.portfolio_link} onChange={(e) => set('portfolio_link', e.target.value)} placeholder="Optional URL" className={inputCls} />
        </Field>
      </div>

      <Field label="Artist bio">
        <textarea data-testid="oc-inline-bio" value={form.artist_bio} onChange={(e) => set('artist_bio', e.target.value)} rows={3} className={textareaCls} placeholder="Optional" />
      </Field>

      <Field label="Artist statement">
        <textarea required data-testid="oc-inline-statement" value={form.artist_statement} onChange={(e) => set('artist_statement', e.target.value)} rows={4} className={textareaCls} />
      </Field>

      <Field label="Artwork link (image / video / proposal)">
        <input required data-testid="oc-inline-artwork" value={form.artwork_link} onChange={(e) => set('artwork_link', e.target.value)} placeholder="URL to portfolio or drive" className={inputCls} />
      </Field>

      <Field label="Will you attend the venue during the exhibition?">
        <select
          required
          data-testid="oc-inline-venue"
          value={form.venue_attendance}
          onChange={(e) => set('venue_attendance', e.target.value)}
          className={inputCls}
        >
          <option value="">— select —</option>
          {ATTENDANCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Field>

      <div className="pt-2">
        {/* GDPR-compliant newsletter opt-in — separate, unchecked by
            default. See services/newsletter.py for the audit trail. */}
        <label className="flex items-start gap-3 mb-4 cursor-pointer group" data-testid="oc-inline-newsletter-optin-label">
          <input
            type="checkbox"
            checked={form.newsletter_opt_in}
            onChange={(e) => set('newsletter_opt_in', e.target.checked)}
            data-testid="oc-inline-newsletter-optin"
            className="mt-1 h-4 w-4 shrink-0 accent-[#90E0EF] cursor-pointer"
          />
          <span className="text-[11px] font-mono text-[#e8e8e8] leading-[1.6] group-hover:text-white transition-colors">
            Yes, I&#39;d like to receive occasional emails from The Relative Gallery about future open calls, exhibitions and artist opportunities.
            <span className="block mt-1 text-[10px] text-[#888]">
              Optional. You can unsubscribe at any time via the link in every email.
            </span>
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          data-testid="oc-inline-submit"
          className="w-full inline-flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 bg-[#90E0EF] text-[#050505] hover:bg-white transition-colors disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? 'Submitting…' : 'Submit application'}
        </button>
        <p className="mt-3 text-[10px] font-mono text-[#888] text-center">
          Your application is reviewed by our curatorial team. Fields marked as optional can be left blank.
        </p>
      </div>
    </form>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Ticket modal — two modes:
//   standard  → name/email/role → warm ticket email from the gallery
//   baf_free  → name/email/role + newsletter/terms → reveal a reactive
//               claim button linking to the British Art Fair.
// ═══════════════════════════════════════════════════════════════════

function RoleSelect({ value, onChange }) {
  return (
    <select
      data-testid="ex-ticket-role"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] focus:outline-none focus:border-[#90E0EF]/40"
    >
      <option value="" disabled>Select one…</option>
      {ROLE_OPTIONS.map((r) => (
        <option key={r.value} value={r.value}>{r.label}</option>
      ))}
    </select>
  );
}

function BafClaimButton({ url }) {
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid="ex-baf-claim-button"
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="group relative block w-full overflow-hidden border border-[#90E0EF] bg-[#90E0EF] text-[#050505] px-6 py-5 text-center"
      style={{ cursor: 'pointer' }}
    >
      <span
        className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out"
        style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)' }}
      />
      <span className="relative flex items-center justify-center gap-2 text-[12px] font-mono uppercase tracking-[0.22em] font-semibold">
        <TicketIcon className="w-4 h-4" />
        Claim your free ticket
        <ExternalLink className="w-3.5 h-3.5" />
      </span>
      <span className="relative mt-1.5 block text-[9px] font-mono tracking-[0.14em] opacity-70">
        on britishartfair.com
      </span>
    </motion.a>
  );
}

function TicketModal({ exhibition, onClose }) {
  const isBaf = exhibition.ticket_mode === 'baf_free';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bafUrl, setBafUrl] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!email.trim()) { toast.error('Email is required'); return; }
    if (!role) { toast.error('Please tell us who you are'); return; }
    if (isBaf && !terms) { toast.error('Please accept the terms & conditions'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/ticket-request?exhibition=${encodeURIComponent(exhibition.slug)}`,
        {
          name: name.trim(),
          email: email.trim(),
          role,
          newsletter_opt_in: newsletter,
          terms_accepted: isBaf ? terms : false,
        },
      );
      setBafUrl(res.data?.baf_ticket_url || exhibition.baf_ticket_url || FALLBACK_BAF_URL);
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // ── BAF success: thank-you + reactive claim button ──
  if (success && isBaf) {
    return (
      <ModalShell onClose={onClose} testId="ex-ticket-modal">
        <div className="py-4 text-center" data-testid="ex-baf-success">
          <Sparkles className="w-9 h-9 text-[#90E0EF] mx-auto" />
          <h3 className="mt-4 text-xl font-outfit font-semibold text-white tracking-tight">Thank you for registering!</h3>
          <p className="mt-3 text-[13px] text-[#cfcfcf] leading-relaxed">
            Your spot with the community is noted. Now claim your <strong className="text-white">free ticket</strong> on the official British Art Fair website — tickets are limited, so grab yours now.
          </p>
          <div className="mt-6">
            <BafClaimButton url={bafUrl} />
          </div>
          <button
            onClick={onClose}
            className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </ModalShell>
    );
  }

  // ── Standard success ──
  if (success) {
    return (
      <ModalShell onClose={onClose} testId="ex-ticket-modal">
        <SuccessScreen
          testId="ex-ticket-success"
          title="You're on the list."
          message="Check your email — we've just sent your ticket from The Relative Gallery."
          onClose={onClose}
        />
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} testId="ex-ticket-modal">
      <ModalHeader
        kicker={isBaf ? 'Free tickets · our treat' : 'Request ticket'}
        title={exhibition.title}
        subtitle={exhibition.theme}
      />

      {isBaf ? (
        <div className="mt-4 space-y-2">
          <p className="text-[13px] text-[#e6e6e6] leading-relaxed">
            We&apos;d love to see you there. As part of <strong className="text-white">The Relative Gallery</strong> community, your tickets to Digitalism at the British Art Fair are <strong className="text-[#90E0EF]">free</strong> — on us.
          </p>
          <p className="text-[11px] font-mono text-[#90E0EF] leading-relaxed tracking-wide">
            Limited tickets · first come, first served · Friday–Sunday only.
          </p>
        </div>
      ) : (
        <p className="mt-3 text-[12px] text-[#cfcfcf] leading-relaxed">
          Register below and we&apos;ll email your ticket with all the details.
        </p>
      )}

      <form onSubmit={submit} className="mt-6 space-y-4">
        <Field label="Your name">
          <input
            data-testid="ex-ticket-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First and last name"
            required
            className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40"
          />
        </Field>
        <Field label="Your email">
          <input
            data-testid="ex-ticket-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40"
          />
        </Field>
        <Field label="Who are you?">
          <RoleSelect value={role} onChange={setRole} />
        </Field>

        {isBaf && (
          <div className="space-y-2.5 pt-1">
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ex-ticket-newsletter">
              <input
                type="checkbox"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
                className="mt-0.5 accent-[#90E0EF] w-3.5 h-3.5"
              />
              <span className="text-[11px] font-mono text-[#aaa] leading-relaxed">
                Keep me in the loop — send me news from The Relative Gallery.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ex-ticket-terms">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 accent-[#90E0EF] w-3.5 h-3.5"
              />
              <span className="text-[11px] font-mono text-[#aaa] leading-relaxed">
                I accept the terms &amp; conditions and privacy policy. <span className="text-[#90E0EF]">*</span>
              </span>
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="ex-ticket-submit"
          className="w-full inline-flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 bg-[#90E0EF] text-[#050505] hover:bg-white transition-colors disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? 'Sending…' : (isBaf ? 'Get my free ticket' : 'Get my ticket')}
        </button>
      </form>
    </ModalShell>
  );
}


// ─── Shared modal primitives ─────────────────────────────────────

const inputCls = 'w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40';
const textareaCls = 'w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40 resize-y';

function ModalShell({ children, onClose, testId, wide }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-[#050505]/85 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid={testId}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full ${wide ? 'max-w-2xl' : 'max-w-md'} bg-[#0a0a0a] border border-[#1a1a1a] p-6 md:p-8`}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 border border-transparent hover:border-[#333] text-[#666] hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </motion.div>
    </motion.div>
  );
}

function ModalHeader({ kicker, title, subtitle }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#90E0EF]">
        {kicker}
      </div>
      <h2 className="mt-2 text-xl font-outfit font-semibold text-white tracking-tight uppercase">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-[#666]">
          — {subtitle}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function SuccessScreen({ title, message, onClose, testId }) {
  return (
    <div className="py-8 text-center" data-testid={testId}>
      <CheckCircle2 className="w-10 h-10 text-[#7ed4a0] mx-auto" />
      <h3 className="mt-4 text-lg font-outfit font-semibold text-white">{title}</h3>
      <p className="mt-2 text-[13px] text-[#f0f0f0] leading-relaxed">{message}</p>
      <button
        onClick={onClose}
        className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors"
      >
        Close
      </button>
    </div>
  );
}
